import {
  ArrowRight,
  Eye,
  Sparkles,
  Cog,
  Workflow,
  ShieldCheck,
  Target,
  Cloud,
  Network,
  Warehouse,
  Truck,
  Brain,
  CheckCircle2,
  Compass,
  PlayCircle,
  Rocket,
  Cpu,
  Activity,
  TrendingUp,
  Bot,
  Building2,
  Layers,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";

const WAREHOUSEX_VISUAL =
  "/images/image-3.webp";

const PLATFORMS_HERO_IMAGE =
  "/images/platforms-hero-banner2.webp";

const BRAND_BLUE = "#025979";
const BRAND_BLUE_DARK = "#03738C";

// --- Atoms -----------------------------------------------------------------
const Eyebrow = ({ children, tone = "blue" }) => (
  <p
    className={`text-[11px] font-semibold tracking-[0.22em] uppercase mb-3 ${
      tone === "purple" ? "text-[#7C3AED]" : "text-[#025979]"
    }`}
  >
    {children}
  </p>
);

const H2 = ({ children, className = "" }) => (
  <h2
    className={`text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#0F172A] leading-[1.1] ${className}`}
    style={{ fontFamily: "Chivo, sans-serif" }}
  >
    {children}
  </h2>
);

const Lead = ({ children, className = "" }) => (
  <p className={`mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl ${className}`}>
    {children}
  </p>
);

const PrimaryButton = ({ href, onClick, children, testId, target, color = "blue" }) => {
  const bg = color === "blue" ? "#2563EB" : BRAND_BLUE;
  const bgHover = color === "blue" ? "#1D4ED8" : BRAND_BLUE_DARK;
  const extra = target ? { target, rel: "noopener noreferrer" } : {};
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      {...extra}
      className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
      style={{ backgroundColor: bg }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = bg)}
    >
      {children}
      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
    </a>
  );
};

const OutlineButton = ({ href, onClick, children, testId, onDark = false, target }) => {
  const extra = target ? { target, rel: "noopener noreferrer" } : {};
  if (onDark) {
    return (
      <a
        href={href} onClick={onClick} {...extra} data-testid={testId}
        className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors"
      >
        {children}
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
      </a>
    );
  }
  return (
    <a
      href={href} onClick={onClick} {...extra} data-testid={testId}
      className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border bg-white transition-colors"
      style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#E0F2FE")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
    >
      {children}
      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
    </a>
  );
};

const SectionDivider = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="h-px bg-gradient-to-r from-transparent via-[#E5E7EB] to-transparent" />
  </div>
);

// --- HERO ------------------------------------------------------------------

const Hero = () => (
  <section
    data-testid="platforms-hero"
    className="relative overflow-hidden text-white"
    style={{
      background:
        "linear-gradient(135deg, #0E1A3F 0%, #112452 35%, #0E1A3F 70%, #0B1734 100%)",
    }}
  >
    {/* Full-bleed ecosystem artwork — sits behind everything as the hero background */}
    <img
      src={PLATFORMS_HERO_IMAGE}
      alt="PrimeS4 AI ecosystem — PRIVA intelligence layer connecting WarehouseX, TransportX and future operational platforms"
      className="absolute inset-0 w-full h-full object-cover object-[72%_center] md:object-right select-none pointer-events-none"
      loading="eager"
      draggable="false"
      data-testid="platforms-hero-artwork"
      aria-hidden="true"
    />
    {/* Soft navy wash on the left so the copy stays legible while the right
        side of the artwork (PRIVA + platforms) reads cleanly. */}
    <div
      className="absolute inset-0 pointer-events-none hidden md:block"
      style={{
        background:
          "linear-gradient(90deg, rgba(11,23,52,0.92) 0%, rgba(14,26,63,0.78) 30%, rgba(14,26,63,0.35) 55%, rgba(14,26,63,0.05) 80%, rgba(14,26,63,0) 100%)",
      }}
    />
    {/* Stronger mobile-only overlay for readability over the busier portion
        of the artwork shown after we shift focus right on small screens. */}
    <div
      className="absolute inset-0 pointer-events-none md:hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(11,23,52,0.92) 0%, rgba(11,23,52,0.78) 45%, rgba(11,23,52,0.62) 100%)",
      }}
    />
    {/* Enterprise grid */}
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.06]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
      }}
    />
    {/* Soft accent glows */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, #1E40AF 0%, transparent 65%)" }} />
      <div className="absolute top-1/3 -right-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }} />
    </div>
    {/* Bottom hairline */}
    <div className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
      style={{ background: "linear-gradient(90deg, transparent 0%, rgba(94,184,216,0.45) 50%, transparent 100%)" }} />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-36 min-h-[88vh] md:min-h-0 flex flex-col justify-center">
      <div className="max-w-2xl" data-testid="platforms-hero-copy">
        <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#5EB8D8] mb-6 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
          Platforms
        </p>
        <h1
          className="text-[34px] sm:text-5xl lg:text-[52px] xl:text-[56px] font-bold tracking-tight leading-[1.05]"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="platforms-hero-headline"
        >
          <span>AI-Native Platforms </span>
          <span className="bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}>
            for Intelligent Enterprise Operations
          </span>
        </h1>
        <p className="mt-8 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl"
          data-testid="platforms-hero-sub">
          Purpose-built operational platforms designed to accelerate SAP-aligned transformation, operational intelligence, automation, and intelligent execution across connected enterprise ecosystems.
        </p>
        <div
          className="mt-12 flex flex-col sm:flex-row sm:flex-wrap gap-3 [&>a]:w-full [&>button]:w-full sm:[&>a]:w-auto sm:[&>button]:w-auto"
          data-testid="platforms-hero-ctas"
        >
          <PrimaryButton href="/warehousex" testId="platforms-hero-cta-warehousex" color="blue">
            Explore WarehouseX
          </PrimaryButton>
          <button
            type="button"
            data-testid="platforms-hero-cta-demo"
            onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
            className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors backdrop-blur-sm"
          >
            Request Demo
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
          </button>
        </div>
        <div className="mt-16 grid sm:grid-cols-3 gap-5 max-w-xl" data-testid="platforms-hero-tags">
          {[
            { icon: ShieldCheck, label: "SAP-Aligned" },
            { icon: Brain, label: "AI-Native" },
            { icon: Target, label: "Enterprise-Grade" },
          ].map((it) => (
            <div key={it.label}
              className="flex items-center gap-3 text-[12px] font-semibold tracking-[0.14em] uppercase text-white/85"
              data-testid={`platforms-hero-tag-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <it.icon className="w-4 h-4 shrink-0 text-[#5EB8D8]" strokeWidth={1.5} />
              <span>{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// --- SECTION 2: ECOSYSTEM OVERVIEW ----------------------------------------
const EcosystemOverview = () => (
  <section
    data-testid="section-platforms-overview"
    className="py-20 lg:py-28 bg-gradient-to-b from-white via-[#FAFAFF] to-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto">
        <Eyebrow tone="purple">Ecosystem Architecture</Eyebrow>
        <H2 className="!mx-auto">Operational Platforms Powered by PRIVA Intelligence</H2>
        <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
          PRIVA acts as the operational intelligence layer across connected enterprise execution platforms.
        </p>
      </div>

      {/* Flow visual */}
      <div className="mt-14 lg:mt-16 max-w-5xl mx-auto" data-testid="platforms-flow">
        {/* PRIVA hub */}
        <div className="flex justify-center">
          <div
            className="relative inline-flex items-center gap-3 px-6 py-3 rounded-sm border"
            style={{
              background:
                "linear-gradient(135deg, rgba(125,211,252,0.06) 0%, rgba(167,139,250,0.10) 100%)",
              borderColor: "rgba(167,139,250,0.4)",
              boxShadow: "0 0 40px rgba(167,139,250,0.18)",
            }}
            data-testid="platforms-flow-priva"
          >
            <div className="w-8 h-8 rounded-sm flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}>
              <Layers className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.6} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#7C3AED]">PRIVA</span>
              <span className="text-[12px] text-[#475569]">Operational Intelligence Layer</span>
            </div>
          </div>
        </div>

        {/* Vertical connector */}
        <div className="relative flex justify-center mt-3 mb-3">
          <div
            className="h-12 w-px"
            style={{
              background:
                "linear-gradient(180deg, rgba(167,139,250,0.7) 0%, rgba(94,184,216,0.5) 100%)",
            }}
          />
        </div>

        {/* Platform tier */}
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { label: "WarehouseX",       sub: "Active",      icon: Warehouse,  active: true,  href: "/warehousex" },
            { label: "TransportX",       sub: "Coming Soon", icon: Truck,      active: false, href: null },
            { label: "Future Platforms", sub: "Expanding",   icon: Compass,    active: false, href: null },
          ].map((p) => {
            const isLink = !!p.href;
            const Wrap = isLink ? "a" : "div";
            return (
              <Wrap
                key={p.label}
                href={isLink ? p.href : undefined}
                data-testid={`platforms-flow-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative flex items-center gap-3 px-5 py-4 rounded-sm border bg-white transition-all ${
                  isLink ? "hover:-translate-y-0.5 hover:shadow-md hover:border-[#5EB8D8]" : "border-[#E5E7EB]"
                } ${p.active ? "border-[#BAE6FD]" : ""}`}
              >
                <div className="w-9 h-9 rounded-sm flex items-center justify-center"
                  style={{
                    background: p.active
                      ? "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)"
                      : "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
                  }}>
                  <p.icon className={`w-4 h-4 ${p.active ? "text-[#025979]" : "text-[#94A3B8]"}`} strokeWidth={1.6} />
                </div>
                <div className="flex flex-col leading-tight flex-1 min-w-0">
                  <span className="text-[14px] font-semibold text-[#0F172A]">{p.label}</span>
                  <span className={`text-[11px] font-semibold tracking-[0.14em] uppercase ${p.active ? "text-[#025979]" : "text-[#94A3B8]"}`}>
                    {p.sub}
                  </span>
                </div>
                {isLink && (
                  <ArrowRight className="w-3.5 h-3.5 text-[#025979] opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
                )}
              </Wrap>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

// --- SECTION 3: WAREHOUSEX -------------------------------------------------
const WAREHOUSEX_EXT = "https://warehousex.primes4.com/";

const WarehouseXSection = () => {
  const pillars = [
    { icon: Target,      label: "SmartStrategy",       desc: "Evaluate, strategize, and prioritize warehouse transformation." },
    { icon: PlayCircle,  label: "SmartExecution",      desc: "Execute warehouse operations with intelligent workflows." },
    { icon: Cpu,         label: "SmartWarehouse Tech", desc: "Automation, robotics, and intelligent warehouse technology." },
    { icon: Rocket,      label: "SmartAcceleration",   desc: "Accelerate adoption, change, and transformation readiness." },
    { icon: Network,     label: "SmartIntegrations",   desc: "Seamless integrations across SAP and enterprise systems." },
  ];
  return (
    <section
      id="warehousex"
      data-testid="section-warehousex"
      className="relative py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Visual */}
          <div className="lg:col-span-6">
            <div
              className="relative rounded-md overflow-hidden border bg-white"
              style={{ borderColor: "#E5E7EB", boxShadow: "0 24px 60px rgba(2,89,121,0.08)" }}
              data-testid="warehousex-visual-frame"
            >
              <div
                className="h-1"
                style={{ background: "linear-gradient(90deg, #5EB8D8 0%, #7C3AED 50%, #5EB8D8 100%)" }}
              />
              <img
                src={WAREHOUSEX_VISUAL}
                alt="WarehouseX — Intelligent Warehouse Transformation Platform"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="warehousex-visual"
              />
            </div>
          </div>

          {/* Copy */}
          <div className="lg:col-span-6">
            <Eyebrow>Flagship Platform</Eyebrow>
            <H2>WarehouseX — Intelligent Warehouse Transformation Platform</H2>
            <Lead>
              AI-native SAP warehouse transformation and operational intelligence platform designed for evaluating, improving, executing, and optimizing warehouse operations.
            </Lead>
            <div className="mt-8">
              <PrimaryButton
                href={WAREHOUSEX_EXT}
                target="_blank"
                testId="warehousex-cta"
                color="blue"
              >
                Explore WarehouseX
              </PrimaryButton>
            </div>
          </div>
        </div>

        {/* Pillars */}
        <div className="mt-16 lg:mt-20">
          <div className="mb-8 max-w-2xl">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#025979] mb-2">Platform Pillars</p>
            <h3 className="text-xl sm:text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Five intelligent pillars driving WarehouseX outcomes
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="warehousex-pillars">
            {pillars.map((p) => (
              <div
                key={p.label}
                className="group bg-white border border-[#E5E7EB] rounded-sm p-5 transition-all duration-200 hover:border-[#5EB8D8] hover:shadow-md hover:-translate-y-0.5"
                data-testid={`warehousex-pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className="w-9 h-9 rounded-sm flex items-center justify-center mb-4"
                  style={{ background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)" }}
                >
                  <p.icon className="w-4 h-4 text-[#025979]" strokeWidth={1.6} />
                </div>
                <h4 className="text-[14px] font-semibold text-[#0F172A] leading-snug">{p.label}</h4>
                <p className="mt-1.5 text-[12px] text-[#475569] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// --- SECTION 4: TRANSPORTX -------------------------------------------------
// Custom enterprise-grade SVG composition — no stock truck imagery.
const TransportXVisual = () => (
  <div
    className="relative w-full aspect-[5/4] max-w-[560px] mx-auto"
    data-testid="transportx-visual"
    aria-hidden="true"
  >
    {/* soft glow */}
    <div
      className="absolute inset-0 blur-3xl opacity-50"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(94,184,216,0.18) 0%, rgba(167,139,250,0.12) 50%, transparent 80%)",
      }}
    />
    <svg viewBox="0 0 560 448" className="absolute inset-0 w-full h-full">
      <defs>
        <linearGradient id="tx-line" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5EB8D8" stopOpacity="0" />
          <stop offset="50%" stopColor="#A78BFA" stopOpacity="0.65" />
          <stop offset="100%" stopColor="#5EB8D8" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Horizon grid */}
      {[110, 160, 220, 290, 370].map((y) => (
        <line key={y} x1="0" y1={y} x2="560" y2={y} stroke="rgba(94,184,216,0.10)" strokeWidth="1" />
      ))}

      {/* 3 lane routes */}
      {[200, 270, 340].map((y, i) => (
        <path
          key={i}
          d={`M 10 ${y} C 160 ${y - 24}, 380 ${y + 30}, 550 ${y - 18}`}
          fill="none"
          stroke="url(#tx-line)"
          strokeWidth="1.4"
          strokeDasharray={i === 1 ? "" : "3 6"}
        />
      ))}

      {/* Route waypoints */}
      {[
        { x: 60,  y: 200, c: "#5EB8D8" },
        { x: 250, y: 184, c: "#A78BFA" },
        { x: 420, y: 248, c: "#5EB8D8" },
        { x: 100, y: 268, c: "#A78BFA" },
        { x: 320, y: 300, c: "#5EB8D8" },
        { x: 480, y: 322, c: "#A78BFA" },
      ].map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="5" fill="#0F1B3D" stroke={p.c} strokeOpacity="0.8" strokeWidth="1.2" />
          <circle cx={p.x} cy={p.y} r="2" fill={p.c} />
        </g>
      ))}
    </svg>

    {/* Floating telemetry cards */}
    <div
      className="absolute top-[6%] left-[6%] flex items-center gap-2 px-3 py-2 rounded-sm border bg-[#0F1B3D]/85 backdrop-blur-sm"
      style={{ borderColor: "rgba(94,184,216,0.4)" }}
    >
      <Truck className="w-3.5 h-3.5 text-[#7DD3FC]" strokeWidth={1.6} />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/80">Fleet</span>
        <span className="text-[13px] font-semibold text-white tabular-nums">128 active</span>
      </div>
    </div>
    <div
      className="absolute top-[8%] right-[6%] flex items-center gap-2 px-3 py-2 rounded-sm border bg-[#0F1B3D]/85 backdrop-blur-sm"
      style={{ borderColor: "rgba(167,139,250,0.45)" }}
    >
      <TrendingUp className="w-3.5 h-3.5 text-[#C4B5FD]" strokeWidth={1.6} />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/80">On-Time</span>
        <span className="text-[13px] font-semibold text-white tabular-nums">96.4%</span>
      </div>
    </div>
    <div
      className="absolute bottom-[8%] left-[8%] flex items-center gap-2 px-3 py-2 rounded-sm border bg-[#0F1B3D]/85 backdrop-blur-sm"
      style={{ borderColor: "rgba(94,184,216,0.4)" }}
    >
      <Workflow className="w-3.5 h-3.5 text-[#7DD3FC]" strokeWidth={1.6} />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/80">Orchestration</span>
        <span className="text-[13px] font-semibold text-white tabular-nums">12 lanes</span>
      </div>
    </div>
    <div
      className="absolute bottom-[10%] right-[10%] flex items-center gap-2 px-3 py-2 rounded-sm border bg-[#0F1B3D]/85 backdrop-blur-sm"
      style={{ borderColor: "rgba(167,139,250,0.45)" }}
    >
      <Activity className="w-3.5 h-3.5 text-[#C4B5FD]" strokeWidth={1.6} />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/80">ETA Signal</span>
        <span className="text-[13px] font-semibold text-white tabular-nums">Live</span>
      </div>
    </div>
  </div>
);

const TransportXSection = () => (
  <section
    id="transportx"
    data-testid="section-transportx"
    className="relative py-20 lg:py-28 overflow-hidden text-white"
    style={{
      background:
        "linear-gradient(135deg, #0E1A3F 0%, #112452 50%, #0B1734 100%)",
    }}
  >
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.06]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
      }}
    />
    <div className="absolute -top-32 right-1/4 w-[420px] h-[420px] rounded-full blur-3xl opacity-20 pointer-events-none"
      style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }} />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#A78BFA] mb-3">Next Platform</p>
          <h2
            className="text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight leading-[1.1]"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="transportx-headline"
          >
            TransportX — Intelligent Transportation Operations
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl">
            Extending operational intelligence and orchestration across transportation, logistics execution, visibility, and connected supply chain operations.
          </p>
          <div
            className="mt-7 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-[11px] font-semibold tracking-[0.18em] uppercase"
            style={{ borderColor: "rgba(167,139,250,0.45)", color: "#C4B5FD", background: "rgba(167,139,250,0.08)" }}
            data-testid="transportx-coming-soon"
          >
            <span className="relative inline-flex w-1.5 h-1.5">
              <span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping" style={{ backgroundColor: "#A78BFA" }} />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#A78BFA" }} />
            </span>
            Coming Soon
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-xl" data-testid="transportx-capabilities">
            {[
              { icon: Eye,      label: "Fleet Visibility" },
              { icon: Workflow, label: "Transport Orchestration" },
              { icon: Activity, label: "Logistics Telemetry" },
              { icon: Network,  label: "Connected Execution" },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-3 text-[13px] text-white/80">
                <div className="w-8 h-8 rounded-sm flex items-center justify-center"
                  style={{ background: "rgba(94,184,216,0.10)", border: "1px solid rgba(94,184,216,0.25)" }}>
                  <c.icon className="w-3.5 h-3.5 text-[#7DD3FC]" strokeWidth={1.6} />
                </div>
                <span>{c.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <TransportXVisual />
        </div>
      </div>
    </div>
  </section>
);

// --- SECTION 5: FUTURE PLATFORM EXPANSION ----------------------------------
const FutureExpansion = () => (
  <section
    data-testid="section-future-platforms"
    className="py-20 lg:py-28 bg-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <Eyebrow tone="purple">Future Expansion</Eyebrow>
        <H2>Expanding the Operational Intelligence Ecosystem</H2>
        <Lead>
          Future operational platforms will extend intelligent orchestration, operational visibility, automation, and enterprise execution across connected business domains.
        </Lead>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="future-platforms-grid">
        {[
          { icon: Compass,    title: "New Operational Domains", desc: "Expanding into adjacent enterprise execution areas." },
          { icon: Building2,  title: "Industry-Aligned Platforms", desc: "Purpose-built for specific industry operations." },
          { icon: Workflow,   title: "Connected Orchestration", desc: "Cross-platform orchestration across enterprise systems." },
          { icon: Brain,      title: "Operational AI Frontier", desc: "Frontier capabilities in autonomous operations." },
        ].map((c) => (
          <div
            key={c.title}
            className="group relative bg-white border border-[#E5E7EB] rounded-sm p-6 transition-all hover:border-[#C4B5FD] hover:shadow-md hover:-translate-y-0.5"
            data-testid={`future-platform-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="w-9 h-9 rounded-sm flex items-center justify-center mb-4"
              style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}>
              <c.icon className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.6} />
            </div>
            <h3 className="text-[14px] font-semibold text-[#0F172A] leading-snug">{c.title}</h3>
            <p className="mt-1.5 text-[12px] text-[#475569] leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// --- SECTION 6: CROSS-PLATFORM CAPABILITIES --------------------------------
const CrossPlatformCapabilities = () => {
  const caps = [
    { icon: Eye,         title: "Operational Visibility" },
    { icon: Sparkles,    title: "AI-Assisted Decisions" },
    { icon: Cog,         title: "Intelligent Automation" },
    { icon: Workflow,    title: "Workflow Orchestration" },
    { icon: Network,     title: "Enterprise Integration" },
    { icon: TrendingUp,  title: "Predictive Intelligence" },
    { icon: ShieldCheck, title: "SAP Alignment" },
    { icon: Bot,         title: "Autonomous Execution" },
  ];
  return (
    <section
      data-testid="section-cross-platform"
      className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow>Cross-Platform</Eyebrow>
          <H2>Capabilities Shared Across Every Platform</H2>
          <Lead>
            A consistent operational intelligence backbone delivered across the ecosystem.
          </Lead>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cross-platform-grid">
          {caps.map((c) => (
            <div
              key={c.title}
              className="group flex items-center gap-3 px-5 py-4 rounded-sm border bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-[#5EB8D8]"
              data-testid={`cross-cap-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)" }}>
                <c.icon className="w-4 h-4 text-[#025979]" strokeWidth={1.6} />
              </div>
              <span className="text-[14px] font-semibold text-[#0F172A]">{c.title}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- SECTION 7: SAP-ALIGNED FOUNDATION -------------------------------------
const SapFoundation = () => {
  const items = [
    { icon: Cloud,        label: "SAP BTP",                 desc: "Built on SAP Business Technology Platform foundations." },
    { icon: Sparkles,     label: "SAP Business AI",         desc: "Aligned with SAP Business AI principles and patterns." },
    { icon: CheckCircle2, label: "Joule Alignment",         desc: "Integrates with SAP Joule for cross-system intelligence." },
    { icon: Network,      label: "Enterprise Integration",  desc: "Connects across enterprise systems and ecosystems." },
    { icon: ShieldCheck,  label: "Governance & Security",   desc: "Enterprise-grade governance, controls, and observability." },
  ];
  return (
    <section
      data-testid="section-platforms-sap"
      className="py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow tone="blue">SAP-Aligned Foundation</Eyebrow>
          <H2>Built on a SAP-Aligned Enterprise AI Foundation</H2>
          <Lead>
            A consistent SAP-aligned foundation underpins every PrimeS4 AI platform — from operational intelligence to integrations and governance.
          </Lead>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="platforms-sap-grid">
          {items.map((it) => (
            <div
              key={it.label}
              className="group bg-white border border-[#BAE6FD] rounded-sm p-5 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-[#0093cb]"
              data-testid={`platforms-sap-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="w-9 h-9 rounded-sm flex items-center justify-center mb-4"
                style={{ background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)" }}>
                <it.icon className="w-4 h-4 text-[#025979]" strokeWidth={1.6} />
              </div>
              <h3 className="text-[14px] font-semibold text-[#0F172A]">{it.label}</h3>
              <p className="mt-1.5 text-[12px] text-[#475569] leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- SECTION 8: CLOSING CTA ------------------------------------------------
const ClosingCta = () => (
  <section
    data-testid="section-platforms-closing"
    className="relative py-24 lg:py-28 text-white overflow-hidden"
    style={{
      background: "linear-gradient(135deg, #0E1A3F 0%, #112452 50%, #0B1734 100%)",
    }}
  >
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #025979 0%, transparent 60%)" }} />
      <div className="absolute bottom-0 right-1/4 w-[360px] h-[360px] rounded-full blur-3xl opacity-15"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 60%)" }} />
    </div>
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#5EB8D8] mb-4 flex items-center justify-center gap-3">
        <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
        Get Started
        <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
      </p>
      <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1]"
        style={{ fontFamily: "Chivo, sans-serif" }}>
        Build Intelligent Operations with PrimeS4 AI Platforms
      </h2>
      <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
        Operational intelligence platforms designed for the future of connected enterprise execution.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <PrimaryButton href="/warehousex" testId="platforms-closing-cta-warehousex" color="blue">
          Explore WarehouseX
        </PrimaryButton>
        <button
          type="button"
          data-testid="platforms-closing-cta-demo"
          onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
          className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors"
        >
          Request Demo
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  </section>
);

const PlatformsPage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="platforms-page">
    <SEO
      title="Enterprise Platforms | PrimeS4 AI"
      description="Explore PrimeS4's enterprise platforms — WarehouseX for SAP-aligned warehouse transformation, TransportX for connected logistics, and a roadmap built on PRIVA AI."
      keywords={[
        "PrimeS4 Platforms",
        "WarehouseX",
        "TransportX",
        "SAP S/4HANA",
        "SAP EWM AI",
        "Enterprise AI platform",
        "Operational Intelligence",
        "Supply chain transformation",
      ]}
      canonical="/platforms"
      imageAlt="PrimeS4 enterprise platforms — WarehouseX, TransportX, and PRIVA AI"
    />
    <Header />
    <main className="flex-1">
      <Hero />
      <EcosystemOverview />
      <SectionDivider />
      <WarehouseXSection />
      <TransportXSection />
      <FutureExpansion />
      <CrossPlatformCapabilities />
      <SapFoundation />
      <ClosingCta />
    </main>
    <Footer />
  </div>
);

export default PlatformsPage;
